import { requestWithAuth } from "./pimClient";

export type PimRaffleEntry = {
  type: "prize" | "lose";
  title: string;
  subtitle: string;
};

type PimRaffleItemResponse = {
  resources?: Array<{
    platform_id?: string;
    name?: string;
    data?: {
      prizes?: PimRaffleEntry[];
      probability?: number;
      times_left_to_play?: number;
    };
  }>;
};

const DEFAULT_RAFFLE_SCHEMA_ID = "1222";
const PRIZES_PLATFORM_ID = "prizes";
const REMOVED_PRIZE_OPTION_PATTERNS = [/free\s+shot/i, /shot\s+vouchers?/i];

const normalizeProbability = (value: unknown) => {
  const parsed = Number(value);

  if (Number.isNaN(parsed)) {
    return 0.1;
  }

  if (parsed < 0) {
    return 0;
  }

  if (parsed > 1) {
    return 1;
  }

  return parsed;
};

export const filterRemovedRafflePrizeOptions = (entries: PimRaffleEntry[]) =>
  entries.filter((entry) => {
    if (entry.type !== "prize") {
      return true;
    }

    const label = `${entry.title} ${entry.subtitle}`;
    return !REMOVED_PRIZE_OPTION_PATTERNS.some((pattern) =>
      pattern.test(label)
    );
  });

export const getPimRaffleConfig = async () => {
  const schemaId =
    process.env.VUE_APP_PIM_RAFFLE_SCHEMA_ID || DEFAULT_RAFFLE_SCHEMA_ID;

  const { data } = await requestWithAuth<PimRaffleItemResponse>({
    method: "get",
    url: "/items",
    params: {
      import_schema_id: schemaId,
      search: PRIZES_PLATFORM_ID,
      _ts: Date.now(),
    },
    headers: {
      "Cache-Control": "no-cache",
      Pragma: "no-cache",
    },
  });

  const matchingResource =
    data.resources?.find(
      (resource) =>
        resource.platform_id === PRIZES_PLATFORM_ID ||
        resource.name === PRIZES_PLATFORM_ID
    ) || data.resources?.[0];
  const config = matchingResource?.data;

  if (!config?.prizes?.length) {
    throw new Error("Missing raffle prize configuration in PIM.");
  }

  const prizes = filterRemovedRafflePrizeOptions(config.prizes);

  if (!prizes.length) {
    throw new Error("Missing raffle prize configuration in PIM.");
  }

  return {
    prizes,
    probability: normalizeProbability(config.probability),
    timesLeftToPlay: Math.max(1, Number(config.times_left_to_play) || 2),
  };
};
